import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

/**
 * Branded confirmation modal — replacement for native window.confirm().
 *
 * Props:
 *  - open: boolean — show/hide
 *  - title, body: ReactNode — heading + descriptive copy
 *  - confirmLabel (default "Delete"), cancelLabel (default "Cancel")
 *  - tone: "danger" | "primary" — colour the confirm button
 *  - busy: boolean — show spinner & disable buttons
 *  - onConfirm, onCancel: callbacks
 *  - testId: optional data-testid root
 */
const ConfirmDialog = ({
  open,
  title = "Are you sure?",
  body,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  tone = "danger",
  busy = false,
  onConfirm,
  onCancel,
  testId = "confirm-dialog",
}) => {
  if (!open) return null;
  const isDanger = tone === "danger";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (!busy && e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className={`w-full max-w-md rounded-xl border bg-spark-ink shadow-2xl overflow-hidden ${isDanger ? "border-red-500/30" : "border-spark-teal/40"}`}>
        <div className={`px-6 py-4 flex items-center gap-3 border-b ${isDanger ? "bg-red-500/10 border-red-500/30" : "bg-spark-teal/10 border-spark-teal/30"}`}>
          <AlertTriangle className={`size-5 ${isDanger ? "text-red-400" : "text-spark-teal"}`} />
          <h2 className={`font-display uppercase tracking-wider text-base font-bold ${isDanger ? "text-red-300" : "text-spark-teal"}`}>{title}</h2>
        </div>
        <div className="px-6 py-5 text-sm text-slate-300 space-y-2">{body}</div>
        <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-xs uppercase tracking-wider font-bold disabled:opacity-60 ${isDanger ? "bg-red-500 hover:bg-red-600" : "bg-spark-teal hover:bg-spark-teal-deep"}`}
            data-testid={`${testId}-confirm`}
          >
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : isDanger ? <Trash2 className="size-3.5" /> : null}
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
